import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Heart, 
  MessageCircle, 
  Users, 
  Lightbulb,
  Award,
  CheckCircle,
  ArrowRight,
  Phone,
  Ear,
  HandshakeIcon
} from 'lucide-react';

const SoftSkillsPage = () => {
  const raisons = [
    {
      icon: Heart,
      title: "L'humain au cœur de la performance",
      description: "Les compétences techniques ne suffisent plus. L'intelligence émotionnelle fait la différence."
    },
    {
      icon: Users,
      title: "Travail en équipe complexifié",
      description: "Télétravail, équipes multigénérationnelles, diversité : les interactions évoluent."
    },
    {
      icon: Lightbulb,
      title: "Innovation et créativité",
      description: "Les soft skills favorisent l'émergence d'idées nouvelles et la résolution de problèmes."
    },
    {
      icon: HandshakeIcon,
      title: "Fidélisation des talents",
      description: "Un environnement bienveillant attire et retient les meilleurs collaborateurs."
    }
  ];

  const competences = [
    {
      title: "Communication assertive",
      description: "Exprimez vos idées avec clarté et conviction, tout en respectant vos interlocuteurs.",
      icon: MessageCircle
    },
    {
      title: "Écoute active",
      description: "Développez une écoute profonde qui crée la confiance et facilite la compréhension.",
      icon: Ear
    },
    {
      title: "Posture professionnelle",
      description: "Adoptez une attitude qui inspire confiance et crédibilité dans toutes les situations.",
      icon: Users
    },
    {
      title: "Gestion des émotions",
      description: "Maîtrisez vos réactions et transformez le stress en énergie positive.",
      icon: Heart
    }
  ];

  const impacts = [
    "Amélioration de la qualité des échanges",
    "Réduction des malentendus et conflits",
    "Renforcement de la cohésion d'équipe",
    "Augmentation de la satisfaction client",
    "Développement de la créativité collective",
    "Meilleure gestion du stress"
  ];

  const formats = [
    {
      title: "Ateliers pratiques",
      duration: "1/2 journée à 2 jours",
      description: "Sessions intensives sur une compétence spécifique avec mises en situation."
    },
    {
      title: "Parcours complet",
      duration: "3 à 5 jours",
      description: "Formation approfondie couvrant l'ensemble des soft skills essentielles."
    },
    {
      title: "Coaching individuel",
      duration: "Sur mesure",
      description: "Accompagnement personnalisé pour développer vos points de progrès."
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#E9DFCF' }} data-testid="soft-skills-page">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-blue-700 to-blue-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium mb-6" style={{ backgroundColor: '#D8C9B3', color: '#2C2A27' }}>
                <Award className="w-4 h-4" />
                Délivrance d'une attestation précisant les compétences visées
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6" style={{ letterSpacing: '-0.04em' }}>
                Soft Skills – L'intelligence relationnelle
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                La compétence invisible qui transforme la relation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/#formulaire">
                  <Button 
                    className="text-white font-semibold py-4 px-8 rounded-full transition-all duration-300 ease-out hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0"
                    style={{ 
                      backgroundColor: '#1C2E4A',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#0F1E33';
                      e.target.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#1C2E4A';
                      e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    Demander un diagnostic stratégique
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:0678269110">
                  <Button 
                    variant="outline" 
                    className="border-white text-white font-medium py-4 px-8 rounded-full transition-all duration-300 ease-out hover:bg-white/10 hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0"
                  >
                    <Phone className="mr-2 w-5 h-5" />
                    Être rappelé
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Pourquoi les soft skills sont stratégiques */}
        <section className="py-16 md:py-24" style={{ backgroundColor: '#E9DFCF' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4" style={{ letterSpacing: '-0.04em' }}>
                Pourquoi les soft skills sont devenues stratégiques
              </h2>
              <p className="text-lg text-gray-600">
                Dans un monde où l'automatisation prend en charge les tâches répétitives, 
                les compétences humaines deviennent le véritable avantage compétitif.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {raisons.map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-blue-700" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Communication, posture, écoute active */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4" style={{ letterSpacing: '-0.04em' }}>
                Communication, posture, écoute active
              </h2>
              <p className="text-lg text-gray-600">
                Les piliers d'une relation professionnelle réussie.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {competences.map((comp, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <comp.icon className="w-6 h-6 text-blue-700" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{comp.title}</h3>
                  <p className="text-gray-600 text-sm">{comp.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Impact sur la performance collective */}
        <section className="py-16 md:py-24" style={{ backgroundColor: '#E9DFCF' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6" style={{ letterSpacing: '-0.04em' }}>
                  Impact sur la performance collective
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Les soft skills ne sont pas qu'un "plus" : elles sont le ciment 
                  qui transforme un groupe d'individus en équipe performante.
                </p>
                <ul className="space-y-4">
                  {impacts.map((impact, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">{impact}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <div className="text-center mb-6">
                  <div className="text-5xl font-black text-blue-700 mb-2">+47%</div>
                  <p className="text-gray-600">d'amélioration de la collaboration</p>
                </div>
                <div className="text-center mb-6">
                  <div className="text-5xl font-black text-blue-700 mb-2">-35%</div>
                  <p className="text-gray-600">de conflits interpersonnels</p>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-black text-blue-700 mb-2">+28%</div>
                  <p className="text-gray-600">de satisfaction au travail</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Format des interventions */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4" style={{ letterSpacing: '-0.04em' }}>
                Format des interventions
              </h2>
              <p className="text-lg text-gray-600">
                Des modalités adaptées à vos contraintes et objectifs.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {formats.map((format, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-xl border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-2">{format.title}</h3>
                  <p className="text-blue-700 font-medium text-sm mb-4">{format.duration}</p>
                  <p className="text-gray-600 text-sm">{format.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-blue-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6" style={{ letterSpacing: '-0.04em' }}>
              Développez les soft skills de vos équipes
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Demandez un diagnostic stratégique gratuit et personnalisé.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/#formulaire">
                <Button 
                  className="bg-white text-blue-700 font-semibold py-4 px-8 rounded-full shadow-lg transition-all duration-[250ms] ease-out hover:bg-gray-100 hover:-translate-y-0.5 hover:shadow-xl active:scale-[0.98] active:translate-y-0"
                >
                  Demander un diagnostic stratégique
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:0678269110">
                <Button 
                  variant="outline" 
                  className="border-white text-white font-medium py-4 px-8 rounded-full transition-all duration-[250ms] ease-out hover:bg-white/10 hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Être rappelé
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SoftSkillsPage;
